import { endOfDay, isBefore, parseISO } from 'date-fns';

import { isTreatedAsOther } from '../../../../../constants/lienholders';
import {
  PayoffRequestStatus,
  useExternalUserPayoffRequestQuery,
  useGetOperatingHoursAndTimeSlotsQuery,
} from '../../../../../gql/generated/graphql';
import { GetLeadByExternalId200 } from '../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { UserDeal } from '../../../utils';
import { PAYOFF_PLAUSIBILITY_BOOK_VALUE_RATIO, PAYOFF_PLAUSIBILITY_FLOOR } from '../constants';

export type PayoffDisplayMode = 'number' | 'updating' | 'status' | 'none';

// Engine-number surfaces pass guidance.payoffVerified; payoffDisplay.amount surfaces pass showsTrustedNumber.
export const payoffLabel = (isConfirmed: boolean): string =>
  isConfirmed ? 'Confirmed payoff' : 'Your payoff';

export const payoffNoun = (isConfirmed: boolean): string =>
  isConfirmed ? 'confirmed payoff' : 'payoff';

export type PayoffDisplay = {
  mode: PayoffDisplayMode;
  amount: number | null;
  goodThroughDate: Date | null;
  sentence: string | null;
  lienholderName: string | null;
  showsTrustedNumber: boolean;
};

const NO_DISPLAY: PayoffDisplay = {
  mode: 'none',
  amount: null,
  goodThroughDate: null,
  sentence: null,
  lienholderName: null,
  showsTrustedNumber: false,
};

const getPayoffWaitingSentence = (
  hasRequestFailed: boolean,
  isDuringTeamHours: boolean,
  lienholderName: string | null,
): string => {
  if (hasRequestFailed) {
    return "We had trouble confirming your payoff. We'll reach out.";
  }

  const opener = `We're working on getting your payoff${
    lienholderName ? ` from ${lienholderName}` : ''
  }.`;

  return isDuringTeamHours
    ? `${opener} Most come back within a few minutes, but some take a bit longer.`
    : `${opener} If we can't retrieve it automatically tonight, our team will pull it first thing on the next business day.`;
};

const isPlausiblePayoff = (amount: number, lendingBookValue: number | null | undefined) =>
  lendingBookValue != null
    ? amount >= lendingBookValue * PAYOFF_PLAUSIBILITY_BOOK_VALUE_RATIO
    : amount >= PAYOFF_PLAUSIBILITY_FLOOR;

const hasGoodThroughPassed = (goodThroughDate: Date | null) =>
  goodThroughDate != null && isBefore(endOfDay(goodThroughDate), new Date());

const usePayoffDisplay = (
  userDeal: UserDeal,
  lead: GetLeadByExternalId200 | undefined,
  { enabled }: { enabled: boolean },
): PayoffDisplay => {
  const dealId = userDeal?.id;
  const { data: payoffRequestData } = useExternalUserPayoffRequestQuery({
    variables: { dealId },
    skip: !enabled || !dealId,
  });
  const { data: operatingHours } = useGetOperatingHoursAndTimeSlotsQuery({ skip: !enabled });

  if (!enabled) {
    return NO_DISPLAY;
  }

  const amount = lead?.payoff ?? null;
  const lienholderName =
    lead?.lienholderName && !isTreatedAsOther({ lienholder_slug: lead.lienholderSlug })
      ? lead.lienholderName
      : null;
  const goodThroughDate = lead?.goodThroughDate ? parseISO(lead.goodThroughDate) : null;

  const isVerified =
    lead?.payoffVerificationStatus === 'verified' ||
    lead?.payoffVerificationStatus === 'verified_by_sales_rep';
  const showsPayoffNumber = amount != null && isPlausiblePayoff(amount, lead?.lendingBookValue);

  if (isVerified && showsPayoffNumber) {
    return {
      mode: hasGoodThroughPassed(goodThroughDate) ? 'updating' : 'number',
      amount,
      goodThroughDate,
      sentence: null,
      lienholderName,
      showsTrustedNumber: true,
    };
  }

  const waitingSentence = getPayoffWaitingSentence(
    payoffRequestData?.externalUserPayoffRequest?.status === PayoffRequestStatus.Failed,
    operatingHours?.getOperatingHoursAndTimeSlots?.isOpen ?? true,
    lienholderName,
  );

  return {
    mode: showsPayoffNumber ? 'number' : 'status',
    amount: showsPayoffNumber ? amount : null,
    goodThroughDate: null,
    sentence: waitingSentence,
    lienholderName,
    showsTrustedNumber: false,
  };
};

export default usePayoffDisplay;
